import { useState, useEffect, useRef } from 'react';
import { api } from '../../../api';
import type { FaqGeneration } from '../../../types';
import { FaqGenerationCard } from './FaqGenerationCard';

interface Props {
  domainId: string;
  domain: string;
  prefillUrl?: string;
}

export function FaqGeneratorSection({ domainId, domain, prefillUrl }: Props) {
  const [generations, setGenerations] = useState<FaqGeneration[]>([]);
  const [loading, setLoading] = useState(true);
  const [sourceUrl, setSourceUrl] = useState(prefillUrl ?? '');
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState('');
  const sectionRef = useRef<HTMLDivElement>(null);

  // Sync URL picker when parent navigates here with a pre-fill
  useEffect(() => {
    if (prefillUrl !== undefined) {
      setSourceUrl(prefillUrl);
      sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [prefillUrl]);

  useEffect(() => {
    api.faqGenerations
      .listByDomain(domainId)
      .then(setGenerations)
      .catch(() => {
        /* non-critical — section stays hidden if fetch fails */
      })
      .finally(() => setLoading(false));
  }, [domainId]);

  async function handleGenerate() {
    const url = sourceUrl.trim();
    if (!url) return;
    setGenError('');
    setGenerating(true);
    try {
      const gen = await api.faqGenerations.create(domainId, { sourceUrl: url });
      setGenerations((prev) => [gen, ...prev]);
      setSourceUrl('');
    } catch (err) {
      setGenError(err instanceof Error ? err.message : 'Generation failed');
    } finally {
      setGenerating(false);
    }
  }

  function handleUpdate(updated: FaqGeneration) {
    setGenerations((prev) => prev.map((g) => (g.id === updated.id ? updated : g)));
  }

  const drafts = generations.filter((g) => g.status === 'draft');
  const approved = generations.filter((g) => g.status === 'approved');
  const discarded = generations.filter((g) => g.status === 'discarded');

  return (
    <section ref={sectionRef} className="bg-white border border-gray-100 rounded-xl p-6 shadow-sm space-y-6">
      {/* Header */}
      <div>
        <h2 className="font-semibold text-brand-ink mb-1">FAQ Generator</h2>
        <p className="text-xs text-gray-400">
          Generate AI-ready FAQ blocks for any page on your site. We extract the page content,
          draft natural Q&amp;A pairs grounded in your copy, and give you reviewed JSON-LD ready
          to paste into your page &lt;head&gt;.
        </p>
      </div>

      {/* Generate form */}
      <div>
        <p className="text-sm font-medium text-brand-ink mb-2">Generate FAQs for a page</p>
        <div className="flex gap-2">
          <input
            type="url"
            value={sourceUrl}
            onChange={(e) => setSourceUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void handleGenerate();
            }}
            placeholder={`https://${domain}/your-page`}
            aria-label="Page URL"
            className="flex-1 text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-brand-green"
          />
          <button
            onClick={() => void handleGenerate()}
            disabled={generating || !sourceUrl.trim()}
            className="text-xs bg-brand-green text-white px-4 py-2 rounded-lg hover:bg-brand-green-dark disabled:opacity-50 transition-colors whitespace-nowrap flex items-center gap-1.5"
          >
            {generating && (
              <span className="w-3 h-3 rounded-full border-2 border-white border-t-transparent animate-spin inline-block" />
            )}
            {generating ? 'Generating…' : 'Generate'}
          </button>
        </div>
        {genError && <p className="text-xs text-red-500 mt-1">{genError}</p>}
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-6">
          <div className="w-4 h-4 rounded-full border-2 border-brand-green border-t-transparent animate-spin" />
        </div>
      )}

      {/* Empty state */}
      {!loading && generations.length === 0 && (
        <div className="text-center py-6 border border-dashed border-gray-200 rounded-xl">
          <p className="text-sm text-gray-500 mb-1">No FAQ generations yet</p>
          <p className="text-xs text-gray-400 max-w-sm mx-auto">
            Enter a page URL above to generate AI-ready FAQ blocks grounded in your page copy.
          </p>
        </div>
      )}

      {/* Drafts */}
      {drafts.length > 0 && (
        <div>
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
            Drafts ({drafts.length})
          </p>
          <div className="space-y-4">
            {drafts.map((g) => (
              <FaqGenerationCard key={g.id} generation={g} onUpdate={handleUpdate} />
            ))}
          </div>
        </div>
      )}

      {/* Approved */}
      {approved.length > 0 && (
        <div>
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
            Approved ({approved.length})
          </p>
          <div className="space-y-4">
            {approved.map((g) => (
              <FaqGenerationCard key={g.id} generation={g} onUpdate={handleUpdate} />
            ))}
          </div>
        </div>
      )}

      {/* Discarded */}
      {discarded.length > 0 && (
        <details>
          <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-600 list-none flex items-center gap-1">
            <span className="inline-block">▶</span> Discarded ({discarded.length})
          </summary>
          <div className="space-y-4 mt-3">
            {discarded.map((g) => (
              <FaqGenerationCard key={g.id} generation={g} onUpdate={handleUpdate} />
            ))}
          </div>
        </details>
      )}
    </section>
  );
}
